import { motion, useReducedMotion } from "framer-motion";
import { Quote, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import { Eyebrow } from "@/components/landing/_primitives";

type Testimonial = {
  quote: { pt: string; en: string };
  author: string;
  role: { pt: string; en: string };
  avatar: string;
  metric: { pt: string; en: string };
  context: { pt: string; en: string };
  accent: string; // hex for soft per-card tint (warm-cream identity)
};

const testimonials: Testimonial[] = [
  {
    quote: {
      pt: "Eu mandava fans pra bio e eles simplesmente sumiam. Reorganizei música, merch e booking — pre-saves no Spotify triplicaram em 30 dias.",
      en: "I was sending fans to my bio and they'd just bounce. Set up my music, merch, and booking link properly — Spotify pre-saves went up 3× in the first month alone.",
    },
    author: "Sarah M.",
    role: { pt: "Artista Musical Independente", en: "Independent Music Artist" },
    avatar: "SM",
    metric: { pt: "+218% pre-saves", en: "+218% pre-saves" },
    context: { pt: "30 dias após a troca", en: "30 days after switching" },
    accent: "#ef7b43",
  },
  {
    quote: {
      pt: "Eu não tinha ideia de qual link gerava receita. Cortei 4 links mortos, dobrei a aposta na comunidade paga, e a receita subiu 40% no mês seguinte.",
      en: "I had zero idea which link was actually driving income. Cut 4 dead links, doubled down on my paid community, and revenue went up 40% the next month.",
    },
    author: "Marcus J.",
    role: { pt: "Creator Digital", en: "Digital Creator" },
    avatar: "MJ",
    metric: { pt: "+40% receita", en: "+40% revenue" },
    context: { pt: "mês seguinte", en: "the following month" },
    accent: "#7d5cff",
  },
  {
    quote: {
      pt: "Minha bio era um caos de links aleatórios e eu perdia vendas todo dia. Hoje tenho uma mini-loja de verdade. Esgotei o primeiro lançamento em 48h.",
      en: "My bio was a mess of random links and I was losing sales every day. Now I have a proper mini-store for my programs. Sold out my first launch in 48 hours.",
    },
    author: "Priya K.",
    role: { pt: "Coach de Fitness", en: "Fitness Coach" },
    avatar: "PK",
    metric: { pt: "Esgotou em 48h", en: "Sold out · 48h" },
    context: { pt: "primeiro lançamento", en: "first launch" },
    accent: "#2e7d50",
  },
];

export function TestimonialsSection() {
  const { language } = useLanguage();
  const rm = useReducedMotion();

  return (
    <section
      id="testimonials"
      className="relative overflow-hidden bg-[#fffaf5] py-20 sm:py-24 lg:py-28"
    >
      {/* Editorial ambient backdrop — token-driven, no mesh gradient */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-24 top-12 h-[420px] w-[420px] rounded-full bg-[#ffd9b8]/35 blur-[140px]" />
        <div className="absolute -right-24 bottom-0 h-[360px] w-[360px] rounded-full bg-[#e8dcff]/40 blur-[140px]" />
      </div>

      <div className="container">
        <motion.div
          initial={rm ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: rm ? 0 : 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <Eyebrow withDot accent="15 90% 58%" className="justify-center">
            {pickByLanguage(language, {
              pt: "Vozes da audiência",
              en: "Voices from the audience",
            })}
          </Eyebrow>
          <h2 className="mt-5 font-editorial font-semibold text-[clamp(2rem,6vw,3.75rem)] leading-[1.05] tracking-[-0.035em] text-[#201814]">
            {pickByLanguage(language, {
              pt: (
                <>
                  Creators que se{" "}
                  <span className="text-gradient-creator">reinventaram</span> aqui.
                </>
              ),
              en: (
                <>
                  Creators who{" "}
                  <span className="text-gradient-creator">reinvented</span> themselves here.
                </>
              ),
            })}
          </h2>
          <p className="mt-5 text-[15px] leading-7 text-[#66584d] sm:text-[16.5px] sm:leading-[1.7]">
            {pickByLanguage(language, {
              pt: "Não são depoimentos de marketing. São pessoas reais que pararam de ser mais um link na bio — e começaram a parecer marca de verdade.",
              en: "Not marketing fluff. Real people who stopped being just another bio link — and started looking like a real brand.",
            })}
          </p>
        </motion.div>

        <div className="mt-14 grid gap-5 sm:gap-6 md:grid-cols-3">
          {testimonials.map((testimonial, index) => (
            <motion.article
              key={testimonial.author}
              initial={rm ? false : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: rm ? 0 : 0.5, delay: index * 0.08 }}
              className="group relative overflow-hidden rounded-[28px] border border-[#eadbcc] bg-white/95 p-6 shadow-[0_18px_45px_rgba(109,77,49,0.06)] transition-shadow duration-300 hover:shadow-[0_28px_70px_rgba(109,77,49,0.10)] sm:p-7"
            >
              {/* Accent hairline (top) — per-card identity */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{
                  background: `linear-gradient(90deg, transparent, ${testimonial.accent}55, transparent)`,
                }}
              />
              {/* Soft accent blob — calmer than gradient */}
              <span
                aria-hidden
                className="pointer-events-none absolute -right-20 -top-20 h-44 w-44 rounded-full opacity-60 blur-3xl transition-opacity duration-500 group-hover:opacity-90"
                style={{ background: `${testimonial.accent}22` }}
              />

              {/* Metric chip — verifiable signal */}
              <div className="flex items-center justify-between">
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em]"
                  style={{
                    borderColor: `${testimonial.accent}55`,
                    color: testimonial.accent,
                    background: `${testimonial.accent}10`,
                  }}
                >
                  <Sparkles className="h-3 w-3" />
                  {pickByLanguage(language, testimonial.metric)}
                </span>
                <Quote
                  className="h-6 w-6 text-[#ef7b43]/25 transition-[opacity] duration-200 group-hover:opacity-60"
                  aria-hidden
                />
              </div>

              {/* Quote — editorial weight, balanced wrap */}
              <p className="mt-5 text-[15px] leading-[1.7] text-[#241b17] [text-wrap:balance]">
                &ldquo;{pickByLanguage(language, testimonial.quote)}&rdquo;
              </p>

              {/* Footer — author + role + context */}
              <div className="mt-6 flex items-center gap-3 border-t border-[#f0e3d7] pt-5">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-semibold uppercase tracking-[0.12em]"
                  style={{
                    background: `${testimonial.accent}14`,
                    color: testimonial.accent,
                    border: `1px solid ${testimonial.accent}33`,
                  }}
                >
                  {testimonial.avatar}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-semibold text-[#241b17]">
                    {testimonial.author}
                  </p>
                  <p className="truncate text-[11.5px] text-[#76675b]">
                    {pickByLanguage(language, testimonial.role)}
                  </p>
                </div>
                <span className="hidden shrink-0 font-mono text-[10px] uppercase tracking-[0.16em] text-[#a08b7c] sm:inline">
                  {pickByLanguage(language, testimonial.context)}
                </span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
